import { useLayoutEffect, useMemo } from 'react'
import { subject } from '@casl/ability'
import { useAbility } from '@casl/react'
import cn from 'classnames'
import { BTN_TXT } from 'constants/txt'
import { Popup } from 'components/Popup'
import { Button, BUTTON_TYPES } from 'components/Button'
import SurveyPreview from 'features/RFP/RFPSurvey/CreateSurvey/SurveyPreview/SurveyPreview'
import { useRFPCreateSurveyContext } from 'features/RFP/RFPSurvey/CreateSurvey/Providers/RFPCreateSurveyContextProvider'
import { useRFPDetailsPopupContext } from 'features/RFP/RFPDetails/Providers/RFPDetailsPopupProvider'
import {
  AbilityContext,
  ACTIONS,
  AppAbility,
  Can,
  ROLES,
  SUBJECTS
} from 'features/Permission'
import { useRFPDetailsContext } from 'features/RFP/RFPDetails/Providers/RFPDetailsContextProvider'
import { safeString } from 'helper/common'
import { useSelector } from 'react-redux'
import { getUser } from 'redux/store/user/getters'

const SubmitSurveyPopup = () => {
  const rfpDetailsContext = useRFPDetailsContext()
  const rfpCreateSurveyContext = useRFPCreateSurveyContext()
  const { submitSurveyPopup } = useRFPDetailsPopupContext()
  const user = useSelector(getUser)
  const ability = useAbility<AppAbility>(AbilityContext)

  const { data } = rfpDetailsContext.state
  const { visible, title } = submitSurveyPopup.state

  const isSourcingOwnerOrClinicalCoordinator = useMemo(() => {
    const surveySubject = subject(SUBJECTS.SURVEY, { ...data })
    return (
      user?.uuid === data.creator?.uuid ||
      user?.role === ROLES.CLINICAL_COORDINATOR ||
      ability.can(ACTIONS.CREATE, surveySubject)
    )
  }, [ability, data, user?.role, user?.uuid])

  const {
    handelCancelSurveyVoting,
    onCategorySwitch,
    checkBeforeForwardSwitch
  } = rfpCreateSurveyContext.actions

  const { isNextButtonEnabled, isPrevButtonEnabled, isSubmitButtonEnabled } =
    rfpCreateSurveyContext.state

  useLayoutEffect(() => {
    submitSurveyPopup.actions.setState((prevState) => ({
      ...prevState,
      title: `${safeString(data.name)} survey`
    }))
  }, [data.name, submitSurveyPopup.actions])

  const Footer = useMemo(
    () => (
      <div
        className={cn('row space-between full-width', {
          'justify-end gap-16': isSourcingOwnerOrClinicalCoordinator
        })}
      >
        <Button
          type={BUTTON_TYPES.DEFAULT}
          onClick={handelCancelSurveyVoting}
          upperCase
        >
          {BTN_TXT.CANCEL}
        </Button>
        <div className="row align-center">
          {!isSourcingOwnerOrClinicalCoordinator && (
            <Button
              type={BUTTON_TYPES.DEFAULT}
              onClick={() => onCategorySwitch('prev')}
              upperCase
              disabled={!isPrevButtonEnabled}
            >
              {BTN_TXT.PREVIOUS}
            </Button>
          )}
          {isNextButtonEnabled && (
            <Button
              type={BUTTON_TYPES.PRIMARY}
              onClick={checkBeforeForwardSwitch}
              upperCase
            >
              {BTN_TXT.NEXT}
            </Button>
          )}
          <Can I={ACTIONS.SUBMIT} a={subject(SUBJECTS.SURVEY, { ...data })}>
            {isSubmitButtonEnabled && (
              <Button
                onClick={checkBeforeForwardSwitch}
                type={BUTTON_TYPES.PRIMARY}
                htmlType="submit"
                form="rfp-timeline-form-id"
                upperCase
              >
                {BTN_TXT.SUBMIT_SURVEY}
              </Button>
            )}
          </Can>
        </div>
      </div>
    ),
    [
      checkBeforeForwardSwitch,
      data,
      handelCancelSurveyVoting,
      isNextButtonEnabled,
      isPrevButtonEnabled,
      isSourcingOwnerOrClinicalCoordinator,
      isSubmitButtonEnabled,
      onCategorySwitch
    ]
  )

  return (
    <Popup
      className="rfp-create-survey-popup"
      visible={visible}
      onCancel={handelCancelSurveyVoting}
      title={title}
      width={910}
      footer={Footer}
    >
      <SurveyPreview />
    </Popup>
  )
}

export default SubmitSurveyPopup
